import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Share2, ShoppingBag, Truck, RotateCcw, ShieldCheck, MessageCircle, ChevronRight, Minus, Plus, Star, MessageSquareText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr, discountPct, cx } from '@/lib/format';
import { useStore } from '@/context/StoreContext';
import Stars from '@/components/common/Stars';
import Loader from '@/components/common/Loader';
import ProductRail from '@/components/common/ProductRail';
import InquiryDialog from '@/components/common/InquiryDialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { applySeo } from '@/lib/seo';

const WHATSAPP_NUMBER = '919000000000';

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, inWishlist, addRecent } = useStore();

  const [product, setProduct] = useState(null);
  const [rel, setRel] = useState({ related: [], frequently_bought_together: [] });
  const [reviews, setReviews] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState({});
  const [showSticky, setShowSticky] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryType, setInquiryType] = useState('photos');
  const ctaRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data); setActiveImg(0); setQty(1);
      const init = {}; (r.data.variants || []).forEach((v) => { init[v.name] = v.options[0]; });
      setVariant(init);
      addRecent(r.data.id);
    }).catch(() => navigate('/shop'));
    api.get(`/products/${slug}/related`).then((r) => setRel(r.data)).catch(() => {});
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get(`/products/${slug}/questions`).then((r) => setQuestions(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      if (!ctaRef.current) return;
      setShowSticky(ctaRef.current.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [product]);

  // SEO: title, meta description, canonical, Open Graph + JSON-LD Product schema
  useEffect(() => {
    if (!product) return undefined;
    const img = (product.images && product.images[0]) || product.cover || '';
    const desc = product.meta_description || product.short_description || product.description || '';
    const cleanupSeo = applySeo({
      title: product.meta_title || `${product.name} — Gaze Decor`,
      description: desc,
      canonical: product.canonical_url,
      ogTitle: product.meta_title || product.name,
      ogDescription: desc,
      ogImage: img,
      ogType: 'product',
    });

    // Structured data (schema.org/Product) for rich search results
    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      image: product.images && product.images.length ? product.images : (img ? [img] : undefined),
      description: desc,
      sku: product.sku || product.id,
      brand: { '@type': 'Brand', name: 'Gaze Decor' },
      offers: {
        '@type': 'Offer',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        priceCurrency: 'INR',
        price: product.price,
        availability: product.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      },
    };
    if (product.num_reviews > 0 && product.rating) {
      schema.aggregateRating = { '@type': 'AggregateRating', ratingValue: product.rating, reviewCount: product.num_reviews };
    }
    let script = document.getElementById('pdp-jsonld');
    if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.id = 'pdp-jsonld'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(schema);

    return () => {
      cleanupSeo && cleanupSeo();
      const s = document.getElementById('pdp-jsonld');
      if (s) s.remove();
    };
  }, [product]);

  const pct = useMemo(() => (product ? discountPct(product.price, product.mrp) : 0), [product]);

  if (!product) return <Loader label="Loading product" />;

  const doAdd = () => { addToCart(product, qty, Object.keys(variant).length ? variant : null); };
  const buyNow = () => { addToCart(product, qty, Object.keys(variant).length ? variant : null); navigate('/checkout'); };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: product.name, url }); } catch { /* */ } }
    else { navigator.clipboard.writeText(url); toast.success('Link copied to clipboard'); }
  };
  const wished = inWishlist(product.id);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="container-gaze py-4">
        <nav className="flex items-center gap-1 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link><ChevronRight size={12} />
          <Link to={`/category/${product.category}`} className="hover:text-foreground">{product.category_name}</Link><ChevronRight size={12} />
          <span className="truncate text-foreground">{product.name}</span>
        </nav>
      </div>

      <div className="container-gaze grid gap-8 pb-10 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <div className="group relative aspect-square overflow-hidden rounded-2xl bg-[hsl(var(--gaze-ivory))]" data-testid="pdp-gallery">
            <img src={product.images[activeImg]} alt={(product.image_alts || [])[activeImg] || product.image_alt || product.name} className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-125" style={{ cursor: 'zoom-in' }} />
            {product.has_360 && <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold">360° View</span>}
            {pct > 0 && <span className="absolute right-3 top-3 rounded-full bg-[hsl(var(--gaze-gold))] px-3 py-1 text-[11px] font-bold text-black">-{pct}%</span>}
          </div>
          <div className="mt-3 flex gap-3">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={cx('h-20 w-20 overflow-hidden rounded-xl border-2', activeImg === i ? 'border-[hsl(var(--gaze-gold))]' : 'border-transparent')}>
                <img src={img} alt={(product.image_alts || [])[i] || product.image_alt || `${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="eyebrow mb-2">{product.category_name}</p>
          <h1 className="text-3xl font-semibold leading-tight sm:text-4xl" data-testid="pdp-title" style={{ fontFamily: 'var(--font-sans)' }}>{product.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <Stars value={product.rating} />
            <span className="text-sm text-muted-foreground">{product.rating} · {product.num_reviews} reviews</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-bold tabular-nums" data-testid="pdp-price">{inr(product.price)}</span>
            {product.mrp > product.price && <span className="text-lg text-muted-foreground line-through">{inr(product.mrp)}</span>}
            {pct > 0 && <span className="text-sm font-semibold text-[hsl(var(--primary))]">{pct}% off</span>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Inclusive of all taxes</p>

          <p className="mt-5 text-sm leading-relaxed text-foreground/80">{product.short_description}</p>

          {/* Variants */}
          {(product.variants || []).map((v) => (
            <div key={v.name} className="mt-5" data-testid="pdp-variant-selector">
              <p className="mb-2 text-sm font-semibold">{v.name}: <span className="font-normal text-muted-foreground">{variant[v.name]}</span></p>
              <div className="flex flex-wrap gap-2">
                {v.options.map((o) => (
                  <button key={o} onClick={() => setVariant((prev) => ({ ...prev, [v.name]: o }))} className={cx('rounded-full border px-4 py-2 text-sm', variant[v.name] === o ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white' : 'border-border bg-white')}>{o}</button>
                ))}
              </div>
            </div>
          ))}

          {/* Stock + qty */}
          <div className="mt-5 flex items-center gap-4">
            {product.in_stock ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))]"><span className="h-2 w-2 rounded-full bg-green-600" /> In Stock{product.stock <= 5 && ` · Only ${product.stock} left`}</span>
            ) : <span className="text-sm font-medium text-destructive">Out of Stock</span>}
            <div className="flex items-center rounded-full border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-9 w-9 place-items-center" aria-label="Decrease"><Minus size={15} /></button>
              <span className="w-8 text-center text-sm font-semibold">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-9 w-9 place-items-center" aria-label="Increase"><Plus size={15} /></button>
            </div>
          </div>

          {/* CTAs */}
          <div ref={ctaRef} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button onClick={doAdd} disabled={!product.in_stock} data-testid="pdp-add-to-cart-button" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[hsl(var(--primary))] bg-white py-3.5 text-sm font-semibold text-[hsl(var(--primary))] transition hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-50">
              <ShoppingBag size={17} /> Add to Cart
            </button>
            <button onClick={buyNow} disabled={!product.in_stock} data-testid="pdp-buy-now-button" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] py-3.5 text-sm font-semibold text-white transition hover:bg-[hsl(var(--gaze-green-bottle))] disabled:opacity-50">
              Buy Now
            </button>
          </div>
          <div className="mt-3 flex gap-3">
            <button onClick={() => toggleWishlist(product)} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium"><Heart size={16} className={wished ? 'fill-destructive text-destructive' : ''} /> {wished ? 'Wishlisted' : 'Wishlist'}</button>
            <button onClick={share} data-testid="pdp-share-button" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium"><Share2 size={16} /> Share</button>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('I have a question about ' + product.name)}`} target="_blank" rel="noreferrer" data-testid="pdp-whatsapp-button" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium"><MessageCircle size={16} className="text-green-600" /> Enquire</a>
          </div>

          {/* Ask about this product — Inquiry entry */}
          <div className="mt-4 rounded-2xl border border-[hsl(var(--gaze-gold))]/40 bg-[hsl(var(--gaze-ivory))] p-4" data-testid="pdp-inquiry-panel">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-[hsl(var(--primary))]"><MessageSquareText size={18} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Want to see the actual product before buying?</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Request real photos or a short video from our team, or ask any question. Genuine purchase help — no bots.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => { setInquiryType('photos'); setInquiryOpen(true); }} data-testid="pdp-ask-photos" className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--primary))] shadow-sm ring-1 ring-inset ring-border">Request real photos</button>
                  <button onClick={() => { setInquiryType('video'); setInquiryOpen(true); }} data-testid="pdp-ask-video" className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--primary))] shadow-sm ring-1 ring-inset ring-border">Request a video</button>
                  <button onClick={() => { setInquiryType('question'); setInquiryOpen(true); }} data-testid="pdp-ask-question" className="rounded-full bg-[hsl(var(--primary))] px-3 py-1.5 text-xs font-semibold text-white">Ask a question</button>
                </div>
              </div>
            </div>
          </div>

          <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} product={product} source="product" defaultType={inquiryType} />

          {/* Trust */}
          <div className="mt-6 grid grid-cols-3 gap-3 rounded-2xl border border-border bg-white p-4 text-center">
            <div><Truck size={20} className="mx-auto text-[hsl(var(--primary))]" /><p className="mt-1 text-xs font-medium">Free Shipping</p></div>
            <div><RotateCcw size={20} className="mx-auto text-[hsl(var(--primary))]" /><p className="mt-1 text-xs font-medium">{product.return_days}-Day Returns</p></div>
            <div><ShieldCheck size={20} className="mx-auto text-[hsl(var(--primary))]" /><p className="mt-1 text-xs font-medium">{product.cod_available ? 'COD Available' : 'Prepaid'}</p></div>
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible defaultValue="desc" className="mt-6">
            <AccordionItem value="desc"><AccordionTrigger>Description</AccordionTrigger><AccordionContent><p className="text-sm leading-relaxed text-foreground/80">{product.description}</p></AccordionContent></AccordionItem>
            <AccordionItem value="material"><AccordionTrigger>Material & Care</AccordionTrigger><AccordionContent><ul className="space-y-1 text-sm text-foreground/80"><li>Material: {product.material}</li><li>Finish: {product.theme}</li><li>Wipe clean with a soft, dry cloth.</li></ul></AccordionContent></AccordionItem>
            <AccordionItem value="dim"><AccordionTrigger>Dimensions</AccordionTrigger><AccordionContent><ul className="space-y-1 text-sm text-foreground/80"><li>Height: {product.height} cm</li><li>Width: {product.width} cm</li><li>Depth: {product.depth} cm</li><li>Weight: {product.weight} kg</li></ul></AccordionContent></AccordionItem>
            <AccordionItem value="delivery"><AccordionTrigger>Delivery & Returns</AccordionTrigger><AccordionContent><p className="text-sm leading-relaxed text-foreground/80">Estimated delivery in 3-7 business days. {product.cod_available ? 'Cash on Delivery available.' : ''} Easy {product.return_days}-day returns on eligible items.</p></AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* FBT */}
      {rel.frequently_bought_together?.length > 0 && (
        <ProductRail testId="rail-fbt" eyebrow="Complete the look" title="Frequently bought together" products={rel.frequently_bought_together} />
      )}

      {/* Reviews + Q&A */}
      <section className="container-gaze py-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <ReviewsBlock slug={slug} reviews={reviews} setReviews={setReviews} product={product} setProduct={setProduct} />
          <QABlock slug={slug} questions={questions} setQuestions={setQuestions} />
        </div>
      </section>

      {rel.related?.length > 0 && <ProductRail testId="rail-related" eyebrow="You may also like" title="Related products" products={rel.related} />}

      {/* Sticky buy bar (mobile) */}
      <AnimatePresence>
        {showSticky && product.in_stock && (
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={{ duration: 0.25 }} data-testid="pdp-sticky-buy-bar" className="fixed inset-x-0 bottom-16 z-30 flex items-center gap-3 border-t border-[hsl(var(--gaze-gold))]/30 bg-[hsl(var(--primary))] px-4 py-3 text-white md:bottom-0">
            <img src={product.images[0]} alt="" className="h-11 w-11 rounded-lg object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{product.name}</p><p className="text-sm font-bold">{inr(product.price)}</p></div>
            <button onClick={doAdd} data-testid="pdp-sticky-add-to-cart-button" className="rounded-full bg-[hsl(var(--gaze-gold))] px-5 py-2.5 text-sm font-semibold text-black">Add to Cart</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ReviewsBlock = ({ slug, reviews, setReviews, product, setProduct }) => {
  const { user } = useStore();
  const [form, setForm] = useState({ rating: 5, title: '', comment: '', name: '' });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.comment.trim()) { toast.error('Please write your review'); return; }
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, form);
      // If awaiting moderation, don't optimistically insert (server returns approved list only)
      if (!data.awaiting_moderation) setReviews((prev) => [data, ...prev]);
      setForm({ rating: 5, title: '', comment: '', name: '' });
      const r = await api.get(`/products/${slug}`); setProduct(r.data);
      toast.success(data.message || 'Thank you for your review!');
    } catch { toast.error('Could not submit review'); }
  };
  return (
    <div data-testid="pdp-reviews">
      <h2 className="text-2xl font-semibold">Ratings & Reviews</h2>
      <div className="mt-4 flex items-center gap-4 rounded-2xl border border-border bg-white p-5">
        <div className="text-center"><p className="text-4xl font-bold">{product.rating}</p><Stars value={product.rating} /><p className="mt-1 text-xs text-muted-foreground">{product.num_reviews} ratings</p></div>
        <div className="h-16 w-px bg-border" />
        <p className="text-sm text-foreground/70">Real reviews from verified Gaze Decor customers.</p>
      </div>
      <form onSubmit={submit} className="mt-5 rounded-2xl border border-border bg-white p-5">
        <p className="mb-2 text-sm font-semibold">Write a review</p>
        <div className="mb-3 flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}><Star size={22} className={n <= form.rating ? 'fill-[hsl(var(--gaze-gold))] text-[hsl(var(--gaze-gold))]' : 'text-muted-foreground/40'} /></button>)}
        </div>
        {!user && <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mb-2 w-full rounded-lg border border-border px-3 py-2 text-sm" />}
        <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Share your experience" rows={3} data-testid="review-comment-input" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
        <button type="submit" data-testid="review-submit-button" className="mt-3 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-semibold text-white">Submit Review</button>
      </form>
      <div className="mt-5 space-y-4">
        {reviews.length === 0 ? <p className="text-sm text-muted-foreground">Be the first to review this product.</p> : reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border bg-white p-4">
            <div className="flex items-center justify-between"><span className="text-sm font-semibold">{r.name}{r.verified && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">Verified</span>}</span><Stars value={r.rating} /></div>
            {r.title && <p className="mt-1 text-sm font-medium">{r.title}</p>}
            <p className="mt-1 text-sm text-foreground/80">{r.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const QABlock = ({ slug, questions, setQuestions }) => {
  const { user } = useStore();
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    try { const { data } = await api.post(`/products/${slug}/questions`, { question: q, name }); if (!data.awaiting_moderation) setQuestions((prev) => [data, ...prev]); setQ(''); toast.success(data.message || 'Question posted'); } catch { toast.error('Could not post question'); }
  };
  return (
    <div data-testid="pdp-qa">
      <h2 className="text-2xl font-semibold">Questions & Answers</h2>
      <form onSubmit={submit} className="mt-4 rounded-2xl border border-border bg-white p-5">
        {!user && <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mb-2 w-full rounded-lg border border-border px-3 py-2 text-sm" />}
        <textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about this product" rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
        <button type="submit" className="mt-3 rounded-full border border-[hsl(var(--primary))] px-5 py-2.5 text-sm font-semibold text-[hsl(var(--primary))]">Post Question</button>
      </form>
      <div className="mt-5 space-y-4">
        {questions.length === 0 ? <p className="text-sm text-muted-foreground">No questions yet. Ask ours experts anything.</p> : questions.map((item) => (
          <div key={item.id} className="rounded-2xl border border-border bg-white p-4">
            <p className="text-sm font-medium">Q: {item.question}</p>
            {item.answers?.length > 0 ? item.answers.map((a, i) => (
              <p key={a?.id || i} className="mt-1 text-sm text-foreground/70">
                A: {typeof a === 'string' ? a : a.text}
                {a?.is_official && <span className="ml-2 rounded-full bg-[hsl(var(--gaze-gold))]/20 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--primary))]">Official</span>}
              </p>
            )) : <p className="mt-1 text-xs text-muted-foreground">Awaiting answer from our team.</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
